/**
 * ISO 3166-1 alpha-2 country → ISO 4217 currency, for picking a visitor's
 * default currency when Firebase Remote Config (`default_currency`, which
 * holds the per-country mapping) can't be reached — ad blockers, privacy
 * browsers and flaky networks block or drop that request. Grouped by
 * currency to keep it compact; countries using a currency of their own are
 * listed under it.
 */
const COUNTRIES_BY_CURRENCY: Record<string, string[]> = {
    EUR: [
        'AD', 'AT', 'AX', 'BE', 'BL', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR',
        'GF', 'GP', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MC', 'ME',
        'MF', 'MQ', 'MT', 'NL', 'PM', 'PT', 'RE', 'SI', 'SK', 'SM', 'TF',
        'VA', 'XK', 'YT'
    ],
    USD: [
        'AS', 'BQ', 'EC', 'FM', 'GU', 'IO', 'MH', 'MP', 'PR', 'PW', 'SV',
        'TC', 'TL', 'UM', 'US', 'VG', 'VI'
    ],
    GBP: ['GB', 'GG', 'IM', 'JE'],
    XOF: ['BF', 'BJ', 'CI', 'GW', 'ML', 'NE', 'SN', 'TG'],
    XAF: ['CF', 'CG', 'CM', 'GA', 'GQ', 'TD'],
    XCD: ['AG', 'AI', 'DM', 'GD', 'KN', 'LC', 'MS', 'VC'],
    XPF: ['NC', 'PF', 'WF'],
    AUD: ['AU', 'CC', 'CX', 'HM', 'KI', 'NF', 'NR', 'TV'],
    NZD: ['CK', 'NU', 'NZ', 'PN', 'TK'],
    DKK: ['DK', 'FO', 'GL'],
    NOK: ['BV', 'NO', 'SJ'],
    CHF: ['CH', 'LI'],
    ZAR: ['ZA'],
    AED: ['AE'],
    AFN: ['AF'],
    ALL: ['AL'],
    AMD: ['AM'],
    AOA: ['AO'],
    ARS: ['AR'],
    AWG: ['AW'],
    AZN: ['AZ'],
    BAM: ['BA'],
    BBD: ['BB'],
    BDT: ['BD'],
    BGN: ['BG'],
    BHD: ['BH'],
    BIF: ['BI'],
    BMD: ['BM'],
    BND: ['BN'],
    BOB: ['BO'],
    BRL: ['BR'],
    BSD: ['BS'],
    BTN: ['BT'],
    BWP: ['BW'],
    BYN: ['BY'],
    BZD: ['BZ'],
    CAD: ['CA'],
    CDF: ['CD'],
    CLP: ['CL'],
    CNY: ['CN'],
    COP: ['CO'],
    CRC: ['CR'],
    CUP: ['CU'],
    CVE: ['CV'],
    ANG: ['CW', 'SX'],
    CZK: ['CZ'],
    DJF: ['DJ'],
    DOP: ['DO'],
    DZD: ['DZ'],
    EGP: ['EG'],
    MAD: ['EH', 'MA'],
    ERN: ['ER'],
    ETB: ['ET'],
    FJD: ['FJ'],
    FKP: ['FK'],
    GEL: ['GE'],
    GHS: ['GH'],
    GIP: ['GI'],
    GMD: ['GM'],
    GNF: ['GN'],
    GTQ: ['GT'],
    GYD: ['GY'],
    HKD: ['HK'],
    HNL: ['HN'],
    HTG: ['HT'],
    HUF: ['HU'],
    IDR: ['ID'],
    ILS: ['IL', 'PS'],
    INR: ['IN'],
    IQD: ['IQ'],
    IRR: ['IR'],
    ISK: ['IS'],
    JMD: ['JM'],
    JOD: ['JO'],
    JPY: ['JP'],
    KES: ['KE'],
    KGS: ['KG'],
    KHR: ['KH'],
    KMF: ['KM'],
    KPW: ['KP'],
    KRW: ['KR'],
    KWD: ['KW'],
    KYD: ['KY'],
    KZT: ['KZ'],
    LAK: ['LA'],
    LBP: ['LB'],
    LKR: ['LK'],
    LRD: ['LR'],
    LSL: ['LS'],
    LYD: ['LY'],
    MDL: ['MD'],
    MGA: ['MG'],
    MKD: ['MK'],
    MMK: ['MM'],
    MNT: ['MN'],
    MOP: ['MO'],
    MRU: ['MR'],
    MUR: ['MU'],
    MVR: ['MV'],
    MWK: ['MW'],
    MXN: ['MX'],
    MYR: ['MY'],
    MZN: ['MZ'],
    NAD: ['NA'],
    NGN: ['NG'],
    NIO: ['NI'],
    NPR: ['NP'],
    OMR: ['OM'],
    PAB: ['PA'],
    PEN: ['PE'],
    PGK: ['PG'],
    PHP: ['PH'],
    PKR: ['PK'],
    PLN: ['PL'],
    PYG: ['PY'],
    QAR: ['QA'],
    RON: ['RO'],
    RSD: ['RS'],
    RUB: ['RU'],
    RWF: ['RW'],
    SAR: ['SA'],
    SBD: ['SB'],
    SCR: ['SC'],
    SDG: ['SD'],
    SEK: ['SE'],
    SGD: ['SG'],
    SHP: ['SH'],
    SLE: ['SL'],
    SOS: ['SO'],
    SRD: ['SR'],
    SSP: ['SS'],
    STN: ['ST'],
    SYP: ['SY'],
    SZL: ['SZ'],
    THB: ['TH'],
    TJS: ['TJ'],
    TMT: ['TM'],
    TND: ['TN'],
    TOP: ['TO'],
    TRY: ['TR'],
    TTD: ['TT'],
    TWD: ['TW'],
    TZS: ['TZ'],
    UAH: ['UA'],
    UGX: ['UG'],
    UYU: ['UY'],
    UZS: ['UZ'],
    VES: ['VE'],
    VND: ['VN'],
    VUV: ['VU'],
    WST: ['WS'],
    YER: ['YE'],
    ZMW: ['ZM'],
    ZWL: ['ZW']
};

const CURRENCY_BY_COUNTRY: Record<string, string> = Object.fromEntries(
    Object.entries(COUNTRIES_BY_CURRENCY).flatMap(([currency, countries]) =>
        countries.map((country) => [country, currency])
    )
);

/** Currency code for an ISO-2 country, or null when unknown. */
export const currencyForCountry = (
    countryCode: string | null | undefined
): string | null =>
    (countryCode && CURRENCY_BY_COUNTRY[countryCode.toUpperCase()]) || null;
